import React, { useEffect, useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import {
  Button,
  Card,
  Form,
  Grid,
  Modal,
  Segment,
  Loader,
  Select,
} from 'semantic-ui-react';
import { useFormContext } from 'react-hook-form';
import {
  type PTPChoices,
  type PTPFormField,
  valuesFromEqChoices,
  getEquipmentFromChoices,
} from './utils';
import { GenericScaledField } from 'src/components/controls/rhf/GenericScaledField';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import get from 'lodash/get';
import { PTPPath } from './ptp-link-type';
import TxFrequencyEndList from './TxFrequencyEndList';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLinkKind } from './hooks';
import {
  fetchFrequencyLists,
  listMissingValue,
  initializeLists,
  useFrequencyState,
  selectionHandler,
  getWarning,
  missingValue,
  checkSelected,
  clearAllValues,
} from './TxFrequencyField.service';
import { identity } from 'src/utils/useful_functions';
import { RootStateOrAny } from 'src/store';
import { openFrequencyModal } from './ptp.reducer';

type Props = PTPFormField<any> & {
  modified: boolean;
  setModified: React.Dispatch<React.SetStateAction<boolean>>;
  choices: PTPChoices;
  disabled: boolean;
  intl: any;
  endName: string;
  precision: number;
  units: string;
  label: string;
  path: PTPPath;
};

function setTxFrequency(setValue, path, value) {
  setValue(`${path}.tx_frequency`, value, {
    shouldDirty: true,
  });
}

function applyStandardFrequencies(state, setValue, radios) {
  for (let i = 0; i < radios.length; i++) {
    setTxFrequency(
      setValue,
      `local.radios.${i}.frequency`,
      state.paths[i].local.value
    );
    setTxFrequency(
      setValue,
      `remote.radios.${i}.frequency`,
      state.paths[i].remote.value
    );
  }
}

function applyMultibandXpicFrequencies(state, setValue) {
  const apply = (endName, from, to) => {
    setTxFrequency(
      setValue,
      `${endName}.radios.${to}.frequency`,
      state.paths[from]?.[endName]?.value ?? null
    );
  };

  for (const endName of ['local', 'remote']) {
    apply(endName, 0, 0);
    apply(endName, 1, 1);
    // Multiband XPIC exposes one Link B picker, but the form has
    // separate Link B1 / Link B2 radios which must share the same value.
    apply(endName, 1, 2);
  }
}

function applySingleFrequency(state, setValue, radios) {
  for (let i = 0; i < radios.length; i++) {
    setTxFrequency(
      setValue,
      `local.radios.${i}.frequency`,
      state.paths[i].local.value
    );
    setTxFrequency(
      setValue,
      `remote.radios.${i}.frequency`,
      state.paths[i].local.value
    );
  }
}

function dedupPathFreqs(endFreqs) {
  // awful hack because of the poor state management of frequency
  // values for 4+0 XPIC. Sometimes 0 and 1 are the correct values
  // and sometimes 0 and 2 are the correct values, depending on
  // whether the path was already saved as XPIC and whether the user
  // has edited the frequencies
  if (endFreqs[0] !== endFreqs[1]) {
    return [endFreqs[0], endFreqs[1]];
  } else {
    return [endFreqs[0], endFreqs[2]];
  }
}

function applyXpic4plus0Frequencies(state, setValue) {
  const apply = (endName, value, to) => {
    setTxFrequency(setValue, `${endName}.radios.${to}.frequency`, value);
  };

  for (const endName of ['local', 'remote']) {
    const endFreqs = dedupPathFreqs(
      state.paths.map((path) => path[endName].value)
    );
    apply(endName, endFreqs[0], 0);
    apply(endName, endFreqs[0], 1);
    apply(endName, endFreqs[1], 2);
    apply(endName, endFreqs[1], 3);
  }
}

function applyFrequenciesToForm(state, setValue, radios, lk, frequencyLists) {
  if (lk === 'xpic_4plus0') {
    applyXpic4plus0Frequencies(state, setValue);
  } else if (lk === 'multi_xpic') {
    applyMultibandXpicFrequencies(state, setValue);
  } else {
    if (frequencyLists.length > 0 && frequencyLists[0].singleFrequency) {
      applySingleFrequency(state, setValue, radios);
    } else {
      applyStandardFrequencies(state, setValue, radios);
    }
  }
}

function FrequencyModalContent(props) {
  const {
    state,
    pathIdx,
    pathFrequencyData,
    setModified,
    methods,
    hilo,
    warning,
    disabled,
  } = props;
  const { setBothValues } = methods;
  // state from reducer - used to get selected values
  const statePath = state.paths[pathIdx];

  return (
    <>
      <Card.Meta className="px-2">{pathFrequencyData.label}</Card.Meta>
      <Grid verticalAlign="middle" padded>
        <Grid.Column width={pathFrequencyData.singleFrequency ? 16 : 8}>
          <TxFrequencyEndList
            list={pathFrequencyData.local.list}
            defaultValue={statePath.local.value}
            label={pathFrequencyData.local.label}
            precision={pathFrequencyData.precision}
            disabled={disabled}
            handleValuesSelection={(e) => {
              setModified(true);
              if (pathFrequencyData.singleFrequency) {
                const value = parseFloat(e.target.value);
                setBothValues(pathIdx, value, null);
              } else {
                // Only try to set the other value when
                // this isn't a single frequency radio
                const [local, remote] = selectionHandler(
                  e,
                  state,
                  'local',
                  pathIdx,
                  pathFrequencyData,
                  hilo
                );
                setBothValues(pathIdx, local, remote);
              }
            }}
          />
        </Grid.Column>

        {!pathFrequencyData.singleFrequency && (
          <Grid.Column width={8}>
            <TxFrequencyEndList
              list={pathFrequencyData.remote.list}
              defaultValue={statePath.remote.value}
              label={pathFrequencyData.remote.label}
              precision={pathFrequencyData.precision}
              disabled={disabled}
              handleValuesSelection={(e) => {
                setModified(true);
                const [local, remote] = selectionHandler(
                  e,
                  state,
                  'remote',
                  pathIdx,
                  pathFrequencyData,
                  hilo
                );
                setBothValues(pathIdx, local, remote);
              }}
            />
          </Grid.Column>
        )}
      </Grid>

      <div style={{ display: 'flex', placeContent: 'center' }}>
        <Button
          onClick={() => setBothValues(pathIdx, null, null)}
          type="button"
        >
          Clear Selection
        </Button>
      </div>

      {warning != null ? (
        <Segment inverted color="red" style={{ marginTop: '1rem' }}>
          {warning}
        </Segment>
      ) : null}
    </>
  );
}

function getFrequencyLayoutColumns(frequencyCount: number) {
  if (frequencyCount === 4) {
    return 2;
  }

  if (frequencyCount >= 1 && frequencyCount <= 3) {
    return frequencyCount;
  }

  return 1;
}

function getFrequencyKeyPathIndex(lk: string, pathIdx: number) {
  if (lk === 'multi_xpic' && pathIdx === 2) {
    return 1;
  }

  return pathIdx;
}

function getTrSpacingCheckIndices(lk: string, pathIndex: number) {
  if (lk === 'multi_xpic') {
    if (pathIndex === 0) {
      return [0];
    }

    return [1, 2];
  }

  // For multiband the radios use different T/R spacings per band
  // (e.g. Link A at 10000 MHz, Link B at 1560 MHz). The pickers map 1:1
  // onto the radios, so each picker must only validate its own radio.
  // Otherwise the Link A picker would test the Link B radios (and vice
  // versa) and wrongly clear every frequency on reload (LPWEB-1224).
  if (
    lk === 'multi_1plus0' ||
    lk === 'multi_co_polar' ||
    lk === 'multi_cross_polar'
  ) {
    return [pathIndex];
  }

  return null;
}

function radiosWillReset(getValues, trSpacing, radioIndices: number[] | null) {
  const indices =
    radioIndices ?? getValues('local.radios').map((_, idx) => idx);

  for (const idx of indices) {
    const [local, remote] = getValues([
      `local.radios.${idx}.frequency.tx_frequency`,
      `remote.radios.${idx}.frequency.tx_frequency`,
    ]);
    if (local && remote && Math.abs(local - remote) !== trSpacing) {
      return true;
    }
  }

  return false;
}

function TxFrequencyField(props: Props) {
  const {
    setModified,
    endName,
    precision,
    units,
    choices,
    disabled,
    label,
    getter,
    path,
    pathIndex,
  } = props;

  const [state, methods] = useFrequencyState(path);

  const { id } = useParams();
  const { isComplex, lk } = useLinkKind();
  const projectId = useSelector(
    (state: RootStateOrAny) => state.mainFrame.projectId
  );
  const { getValues } = useFormContext();

  const [
    band,
    product,
    linkProtection,
    regulation,
    hilo,
    trSpacing,
    bandwidth,
  ] = valuesFromEqChoices(getEquipmentFromChoices(choices, pathIndex), [
    'band',
    'product',
    'link_protection',
    'regulation',
    'hi',
    'tr_spacing',
    'bandwidth',
  ]);

  const computeFreqKeys = () => ({
    local: state.paths.map((_, i) => {
      const keyPathIdx = getFrequencyKeyPathIndex(lk, i);
      const keyPath = state.paths[keyPathIdx];
      return (
        keyPath?.local.value ??
        getValues(`local.radios.${keyPathIdx}.frequency.tx_frequency`)
      );
    }),
    remote: state.paths.map((_, i) => {
      const keyPathIdx = getFrequencyKeyPathIndex(lk, i);
      const keyPath = state.paths[keyPathIdx];
      return (
        keyPath?.remote.value ??
        getValues(`remote.radios.${keyPathIdx}.frequency.tx_frequency`)
      );
    }),
  });
  const raster = state.raster;

  // Snapshot freq values for the queryKey while the modal is closed, so it
  // doesn't change on every setValue cascade inside the choices mutation's
  // onSuccess — that cascade would otherwise fire a storm of this heavy
  // POST /frequency_list (LPWEB-1213).
  // useMemo (not useState/useEffect) so the snapshot updates in the same
  // render as `choices`, avoiding a second refetch. `state.showPopup` is a dep
  // so that closing the modal re-syncs the snapshot to the frequencies just
  // applied, rather than flipping the key back to its stale pre-modal value.
  const snapshotFreqKeys = useMemo(
    computeFreqKeys,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [choices, state.showPopup]
  );

  // While the modal is open the in-progress selections have to drive the key.
  // The backend narrows each list from the pairs already chosen (see
  // apply_transmit_frequencies and other_end_frequency in the frequency_list
  // view), so on a multi-pair link such as 4+0 the remaining lists must
  // refetch as soon as the first pair is picked (LPWEB-1245). The equipment
  // cascade can't run while the modal is open, so reading live values here
  // doesn't reintroduce LPWEB-1213.
  const freqKeys = state.showPopup ? computeFreqKeys() : snapshotFreqKeys;

  let queryKey = [
    projectId,
    'ptp',
    id,
    'frequencies',
    band,
    product,
    linkProtection,
    regulation,
    hilo,
    trSpacing,
    bandwidth,
    raster,
    ...freqKeys.local,
    ...freqKeys.remote,
  ];

  const {
    data: frequencyListData,
    isLoading,
    isPreviousData,
    isFetching,
  } = useQuery({
    queryKey,
    keepPreviousData: true,
    queryFn: () => {
      // Read live values inside the queryFn so that the request payload
      // always reflects the latest form state, even when the snapshot in the
      // queryKey is older (e.g. after a Clear All that removes the query).
      const live = computeFreqKeys();
      return fetchFrequencyLists(
        projectId,
        id,
        live.local,
        live.remote,
        choices,
        raster
      );
    },
  });

  // Memoise the (potentially huge) list expansion so it only rebuilds when the
  // fetched data actually changes, not on every render. This also keeps the
  // per-end list array references stable, which the virtualised picker relies
  // on to avoid rebuilding its row data each render (LPWEB-1124).
  const frequencyLists = useMemo(
    () => initializeLists(frequencyListData),
    [frequencyListData]
  );

  if (isLoading) {
    return (
      <Form.Field>
        <p>Loading...</p>
      </Form.Field>
    );
  }

  if (frequencyListData == null) {
    // the frequency list request failed - don't crash the panel
    return (
      <Form.Field>
        <p>Unable to load the frequency list</p>
      </Form.Field>
    );
  }

  const frequencyCount = frequencyListData?.frequencies?.length ?? 0;

  return (
    <TxFrequencyDisplay
      state={state}
      setModified={setModified}
      methods={methods}
      endName={endName}
      pathIndex={pathIndex}
      isComplex={isComplex}
      rasterChoices={frequencyListData.raster_choices}
      rasterLabel={frequencyListData.raster_label ?? 'Raster'}
      rasterUnits={frequencyListData.raster_units ?? 'MHz'}
      displayPrecision={frequencyListData.display_precision}
      frequencyLists={frequencyLists}
      frequencyCount={frequencyCount}
      hilo={hilo}
      trSpacing={trSpacing}
      label={label}
      getter={getter}
      units={units}
      precision={precision}
      choices={choices}
      disabled={disabled}
      isPreviousData={isPreviousData}
      isFetching={isFetching}
    />
  );
}

/**
 * Split the components so that we can run useEffect on the api list data
 */
function TxFrequencyDisplay(props) {
  const {
    state,
    setModified,
    methods,
    endName,
    pathIndex,
    isComplex,
    frequencyLists,
    frequencyCount,
    rasterChoices,
    rasterLabel,
    rasterUnits,
    displayPrecision,
    hilo,
    trSpacing,
    label,
    getter,
    units,
    precision,
    choices,
    disabled,
    isPreviousData,
    isFetching,
  } = props;
  const { setAllValues, closePopup, togglePopup, setRaster } = methods;

  useEffect(() => {
    if (!rasterChoices.includes(state.raster)) {
      setRaster(rasterChoices[0]);
    }
  }, [rasterChoices]);

  const dispatch = useDispatch();
  const { id } = useParams();
  const { lk } = useLinkKind();
  const qc = useQueryClient();
  const {
    getValues,
    setValue,
    formState: { dirtyFields },
  } = useFormContext();

  const currentFrequencyEnd = useSelector(
    (state) => state.ptp.currentFrequencyEnd
  );
  const permissionWrite = useSelector(
    (state: any) => state.mainFrame.permissionWrite
  );

  const anyFixed = frequencyLists
    .map((list) => {
      if (list.remote) {
        return [list.local.fixed, list.remote.fixed];
      } else {
        return [list.local.fixed];
      }
    })
    .flat()
    .some(identity);

  useEffect(() => {
    // reset selected frequencies when tr spacing changes
    // but dont try to reset when the lists are "fixed" (e.g. L6)
    if (trSpacing !== 'Any' && trSpacing !== null && !anyFixed) {
      const radioIndices = getTrSpacingCheckIndices(lk, pathIndex);

      if (radiosWillReset(getValues, trSpacing, radioIndices)) {
        clearAllValues(getValues, setValue, setAllValues, true);
      }
    }
  }, [trSpacing, anyFixed, lk, pathIndex]);

  useEffect(() => {
    if (isFetching || isPreviousData) {
      return;
    }

    const hasDirtyFrequency = ['local', 'remote'].some((endName) =>
      get(
        dirtyFields,
        `${endName}.radios.${pathIndex}.frequency.tx_frequency`,
        false
      )
    );

    const endMatches =
      currentFrequencyEnd.endName != null &&
      currentFrequencyEnd.pathIndex != null &&
      currentFrequencyEnd.endName === endName &&
      currentFrequencyEnd.pathIndex === pathIndex;

    // don't want this useEffect to interfere with
    // user selecting values in lists
    // or when the end/path that was changed is out of sync
    // with the other ends/paths
    if (!state.showPopup && hasDirtyFrequency && endMatches) {
      for (const endName of ['local', 'remote']) {
        let index = pathIndex;
        if (lk === 'xpic_4plus0') {
          if (pathIndex === 0 || pathIndex === 1) {
            index = 0;
          } else {
            index = 1;
          }
        } else if (lk === 'multi_xpic') {
          // B1 (radio 1) and B2 (radio 2) share a single Link B list, so
          // validate B2 against that list rather than letting it fall
          // through the guard below unvalidated. Mirrors the same remap
          // already done for the query key in getFrequencyKeyPathIndex.
          index = getFrequencyKeyPathIndex(lk, pathIndex);
        }

        // Guard against indexing past the available lists. multi_xpic maps
        // its extra B2 radio back onto an existing list (index 1), so it
        // guards on the resolved index; other kinds keep the pathIndex bail
        // so shipped behaviour (e.g. xpic_4plus0) is unchanged.
        const guardIndex = lk === 'multi_xpic' ? index : pathIndex;
        if (guardIndex >= frequencyLists.length) {
          // XPIC has 2 paths but only one set of freqs
          break;
        }

        const freqList = frequencyLists[index];
        if (endName in freqList) {
          // Some products only have a single frequency selection
          // so remote may not be set
          const { list } = freqList[endName];
          const txPath = `${endName}.radios.${pathIndex}.frequency.tx_frequency`;
          if (listMissingValue(list, getValues(txPath))) {
            clearAllValues(getValues, setValue, setAllValues, true);
            break;
          }
        }
      }
    }
  }, [
    state.showPopup,
    pathIndex,
    endName,
    currentFrequencyEnd,
    isFetching,
    isPreviousData,
    JSON.stringify(dirtyFields),
    JSON.stringify(frequencyLists[pathIndex]?.local.list ?? {}),
    // Remove may not exist if the link uses a single frequency
    JSON.stringify(frequencyLists[pathIndex]?.remote?.list ?? {}),
  ]);

  useEffect(() => {
    // due to network delay, its sometimes possible to have duplicates
    // selected - if thats the case just clear everything
    if (!(isComplex && frequencyLists.length === 1)) {
      // 4+0 xpic, 2+2 SD and 2+2 XPIC all
      // intentionally duplicate A,B and C,D
      if (
        lk !== 'xpic_4plus0' &&
        lk !== 'sd_2plus2' &&
        lk !== 'xpic_2plus2' &&
        lk !== 'multi_xpic'
      ) {
        const localValues = state.paths
          .map((p) => p.local.value)
          .filter((v) => v != null);
        const remoteValues = state.paths
          .map((p) => p.remote.value)
          .filter((v) => v != null);
        const ls = new Set(localValues);
        const rs = new Set(remoteValues);

        if (ls.size !== localValues.length || rs.size !== remoteValues.length) {
          clearAllValues(getValues, setValue, setAllValues, false);
        }
      }
    }
  }, [isComplex, frequencyLists.length, JSON.stringify(state.paths)]);

  function closeModal() {
    setAllValues(null);
    closePopup();
  }

  // when the "clear all" button is pressed
  function handleClearAll() {
    if (isComplex) {
      // for complex (multiple paths), removing the query gives better
      // responsiveness in the ui when pressing "clear all"
      // for non-complex it will cause a flicker since we don't persist
      // the query whilst refetching - therefore only remove when complex
      qc.removeQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return key.includes('frequencies') && key.includes('ptp');
        },
      });
    }
    clearAllValues(getValues, setValue, setAllValues, true);
  }

  function handleOk() {
    applyFrequenciesToForm(
      state,
      setValue,
      getValues('local.radios'),
      lk,
      frequencyLists
    );
    closeModal();
  }

  // True whenever the lists on screen are stale: either a refetch is in
  // flight, or keepPreviousData is showing the results of an older query key.
  const isUpdating = isPreviousData || isFetching;

  const modalColumns = getFrequencyLayoutColumns(frequencyCount);
  const isDirty = get(dirtyFields, getter, false);

  const warnings = frequencyLists.map(getWarning(state));
  const anyWarning = warnings.some((w) => w != null);
  const anyMissing = [...Array(state.paths.length).keys()].some(
    missingValue(state, frequencyLists)
  );
  const onlySomeSelected = checkSelected(state, frequencyLists);

  return (
    <Form.Field>
      <GenericScaledField
        label={label}
        getter={getter}
        units={units}
        precision={displayPrecision ?? precision}
        choices={choices}
        disabled={disabled}
        recalc={isDirty}
        watch={id}
      >
        <Button
          color="blue"
          className="ml-1"
          type="button"
          onClick={() => {
            dispatch(openFrequencyModal({ endName, pathIndex }));
            togglePopup();
          }}
          disabled={!permissionWrite}
        >
          Select...
        </Button>
      </GenericScaledField>

      <Modal
        size={
          frequencyCount === 1
            ? 'mini'
            : frequencyCount === 3
              ? 'large'
              : 'small'
        }
        open={state.showPopup}
        onClose={closeModal}
      >
        <Modal.Header>
          <Button
            circular
            icon="close"
            title="Close"
            onClick={closeModal}
            floated="right"
          />
          {isUpdating ? (
            <div style={{ display: 'flex' }}>
              <span style={{ flex: '1 1 0%' }}>
                Select Transmit Frequencies
              </span>
              <span>Updating list...</span>
            </div>
          ) : (
            <>Select Transmit Frequencies</>
          )}
        </Modal.Header>

        <Modal.Content>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${modalColumns}, minmax(0, 1fr))`,
              // Busy cursor over the gaps between the lists as well as the
              // lists themselves (the rows set their own cursor, see App.css).
              cursor: isUpdating ? 'progress' : undefined,
            }}
            aria-busy={isUpdating}
          >
            {frequencyLists.map((freqList, i) => {
              if (state.paths[i] == null) {
                // without this, the UI will crash sometimes
                // when changing Link Type
                return <Loader active />;
              }

              return (
                <div key={`freq-modal-${i}`}>
                  <FrequencyModalContent
                    pathIdx={i}
                    state={state}
                    setModified={setModified}
                    pathFrequencyData={freqList}
                    methods={methods}
                    hilo={hilo}
                    warning={warnings[i]}
                    // Block selection while the shown list is stale. The
                    // backend narrows every list from the pairs already chosen
                    // (LPWEB-1245), so a click landing on a pre-refetch list
                    // can pick a frequency the refresh was about to remove.
                    // LPWEB-1224 previously forced this to false because a
                    // dropped click gave no feedback; the busy cursor plus the
                    // "Updating list..." header now make the wait visible
                    // instead of silently swallowing the click.
                    disabled={isUpdating}
                  />
                </div>
              );
            })}
          </div>

          {rasterChoices.length > 1 && (
            <div
              style={{
                marginTop: '1em',
              }}
            >
              <label
                style={{
                  display: 'block',
                }}
              >
                {rasterLabel}
              </label>
              <Select
                value={state.raster}
                options={rasterChoices.map((r) => {
                  return { key: r, text: `${r} ${rasterUnits}`, value: r };
                })}
                onChange={(e, data) => {
                  setRaster(Number(data.value));
                }}
              ></Select>
            </div>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button type="button" onClick={handleClearAll}>
            Clear All
          </Button>
          <Button type="button" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            type="button"
            color="blue"
            disabled={anyWarning || anyMissing || onlySomeSelected}
            onClick={handleOk}
          >
            OK
          </Button>
        </Modal.Actions>
      </Modal>
    </Form.Field>
  );
}

export default injectIntl(TxFrequencyField);
